import {
    Box,
    Button,
    Skeleton,
    Stack,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import * as Yup from 'yup';
import {
    Check,
    ChevronDown,
    CreditCard,
    Layers,
} from 'lucide-react';
import { httpClient } from '../../common/httpclient';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { sidebarExpandedSelector } from '../../component/layout/sidebarState';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import MobileCardMachineForm from '../../component/cardmachine/MobileCardMachineForm';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';

const MAX_INSTALLMENT_LIMIT = 12;

function parseApiErrorPayload(data) {
    if (data == null) return 'Erro desconhecido';
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
        return data.message || data.error || data.id || 'Erro ao processar a solicitação';
    }
    return String(data);
}

function parseFeeInput(value) {
    if (value === null || value === undefined || value === '') return NaN;
    return parseFloat(String(value).replace(',', '.'));
}

function feeToInputValue(value) {
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function pct(value) {
    return Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';
}

// Regenera a matriz de taxa por parcela (1x..max): preserva os valores das
// linhas que continuam existindo, adiciona linhas novas ao final com uma taxa
// sugerida (taxa de débito + incremento progressivo) e remove as últimas ao
// diminuir — mesma lógica de `regenerateRatesTable()`/`genDefaultRates()` de
// `prototipo-lista-maquininhas.html`.
function regenerateRates(max, currentRates, debitFeeRaw) {
    const debitFeeVal = parseFeeInput(debitFeeRaw);
    const baseDebit = Number.isNaN(debitFeeVal) ? 1.3 : debitFeeVal;
    const newRates = [];
    for (let i = 1; i <= max; i++) {
        const existing = currentRates.find((r) => r.installment_number === i);
        if (existing) {
            newRates.push(existing);
        } else {
            const prevTax = newRates.length ? parseFeeInput(newRates[newRates.length - 1].tax) : NaN;
            const suggested = !Number.isNaN(prevTax) ? +(prevTax + 0.18).toFixed(2) : +(baseDebit + 0.9).toFixed(2);
            newRates.push({ installment_number: i, tax: feeToInputValue(suggested) });
        }
    }
    return newRates;
}

/**
 * Seção colapsável do formulário de inserção (DESIGN-SYSTEM.md §5.5
 * "Accordion insertion form" / §6.2) — copiada 1:1 de `products/Form.js` /
 * `services/Form.js`.
 */
function AccordionSection({ open, complete, icon: Icon, title, subtitle, onToggle, children }) {
    const theme = useTheme();
    return (
        <Box
            sx={{
                border: '1.5px solid',
                borderColor: open ? 'primary.main' : 'divider',
                borderRadius: `${theme.customRadius.hero}px`,
                overflow: 'hidden',
                backgroundColor: 'background.paper',
                boxShadow: open ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                transition: 'border-color .2s, box-shadow .2s',
            }}
        >
            <Box
                onClick={onToggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.75, p: '18px 20px', cursor: 'pointer' }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        backgroundImage: theme.palette.gradient.brand,
                        color: '#fff',
                    }}
                >
                    <Icon size={19} />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
                    <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
                </Box>
                {complete && (
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: 'success.main',
                            color: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Check size={12} strokeWidth={3} />
                    </Box>
                )}
                <ChevronDown
                    size={16}
                    color={theme.palette.text.disabled}
                    style={{ transition: 'transform .2s', transform: open ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                />
            </Box>
            <Box sx={{ maxHeight: open ? 3000 : 0, overflow: 'hidden', transition: 'max-height .35s ease' }}>
                <Box sx={{ p: '16px 20px 24px' }}>{children}</Box>
            </Box>
        </Box>
    );
}

function PreviewRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12.5px', py: '6px', color: 'text.secondary' }}>
            <span>{label}</span>
            <Box component="b" sx={{ color: 'text.primary', textAlign: 'right' }}>{value}</Box>
        </Box>
    );
}

export default function Form() {
    const theme = useTheme();
    const isSidebarMobile = useMediaQuery('(max-width:1023.95px)');
    const isSidebarExpanded = useRecoilValue(sidebarExpandedSelector);
    const actionFooterLeft = isSidebarMobile ? 0 : (isSidebarExpanded ? 264 : 76);

    const navigate = useNavigate();
    const { id } = useParams();
    const isMobileShell = useIsMobileShell();
    useAppBarTitle(isMobileShell && id ? 'Editar maquininha' : '');

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState('');

    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(false);
    // Seções do acordeão abertas de forma independente (não exclusiva): cada
    // seção guarda seu próprio estado de expansão, permitindo múltiplas
    // seções abertas ao mesmo tempo.
    const [openSections, setOpenSections] = useState(['mach-1']);
    const isSectionOpen = (key) => openSections.includes(key);
    const toggleSection = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    ));
    const ensureSectionOpen = (key) => setOpenSections((prev) => (
        prev.includes(key) ? prev : [...prev, key]
    ));

    const [installmentRates, setInstallmentRates] = useState([]);

    const validationSchema = Yup.object({
        name: Yup.string().required('Nome é obrigatório'),
        maxInstallment: Yup.number()
            .required('Máximo de parcelas é obrigatório')
            .min(1, 'Mínimo de 1 parcela')
            .max(MAX_INSTALLMENT_LIMIT, `Máximo de ${MAX_INSTALLMENT_LIMIT} parcelas`),
        tax_debit: Yup.string()
            .required('Taxa de débito é obrigatória')
            .test('tax_debit', 'Taxa deve ser maior que 0,00', value => {
                if (!value) return false;
                const numValue = Number(value.replace(',', '.'));
                return numValue >= 0;
            }),
        tax_pixqrcode: Yup.string()
            .required('Taxa PIX é obrigatória')
            .test('tax_pixqrcode', 'Taxa deve ser maior que 0,00', value => {
                if (!value) return false;
                const numValue = Number(value.replace(',', '.'));
                return numValue >= 0;
            })
    });

    const handleShowSuccessSnackbar = () => {
        setOpenSnackbar(true);
        setMessage(id ? 'Maquininha atualizada com sucesso!' : 'Maquininha cadastrada com sucesso!');
        setSeverity('success');
    };

    const handleShowErrorSnackbar = (responseData) => {
        setOpenSnackbar(true);
        setMessage(parseApiErrorPayload(responseData));
        setSeverity('error');
    };

    const formik = useFormik({
        initialValues: {
            name: '',
            maxInstallment: '',
            tax_debit: '',
            tax_pixqrcode: '',
        },
        validationSchema: validationSchema,
        onSubmit: () => {
            if (id) {
                handleUpdateCardMachine();
            } else {
                handleCreateCardMachine();
            }
        },
    });

    const buildInstallmentTaxPayload = () => installmentRates.map((rate) => ({
        installment_number: rate.installment_number,
        tax: (rate.tax && String(rate.tax).trim()) || '0,00',
    }));

    const handleCreateCardMachine = async () => {
        setLoading(true);
        try {
            const data = {
                name: formik.values.name,
                max_amount_installment: parseInt(formik.values.maxInstallment, 10),
                tax_debit: formik.values.tax_debit,
                tax_pixqrcode: formik.values.tax_pixqrcode,
                installment_tax: buildInstallmentTaxPayload(),
            };

            const response = await httpClient.post('/cardmachines', data);
            if (response.status !== 201) {
                handleShowErrorSnackbar(response.data);
                return;
            }
            handleShowSuccessSnackbar();
            formik.resetForm();
            setInstallmentRates([]);
            setOpenSections(['mach-1']);
        } catch (error) {
            console.error('Error creating card machine:', error);
            const payload = error.response?.data;
            handleShowErrorSnackbar(
                payload !== undefined ? payload : (error.message || 'Não foi possível cadastrar a maquininha.')
            );
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateCardMachine = async () => {
        setLoading(true);
        try {
            const data = {
                name: formik.values.name,
                max_amount_installment: parseInt(formik.values.maxInstallment, 10),
                tax_debit: formik.values.tax_debit,
                tax_pixqrcode: formik.values.tax_pixqrcode,
                installment_tax: buildInstallmentTaxPayload(),
            };

            const response = await httpClient.put(`/cardmachines/${id}`, data);
            if (response.status === 204) {
                handleShowSuccessSnackbar();
            } else {
                handleShowErrorSnackbar(response.data);
            }
        } catch (error) {
            console.error('Error updating card machine:', error);
            const payload = error.response?.data;
            handleShowErrorSnackbar(
                payload !== undefined ? payload : (error.message || 'Não foi possível atualizar a maquininha.')
            );
        } finally {
            setLoading(false);
        }
    };

    const fetchCardMachine = async () => {
        setLoadingData(true);
        try {
            const response = await httpClient.get(`/cardmachines/${id}`);
            if (response.status === 200) {
                const data = response.data;
                formik.setValues({
                    name: data.Name || '',
                    maxInstallment: data.MaxAmountInstallment || '',
                    tax_debit: data.TaxDebit || '',
                    tax_pixqrcode: data.TaxPixQrCode || '',
                });
                setInstallmentRates((data.InstallmentCreditTax || []).map((item) => ({
                    installment_number: item.installment_number,
                    tax: item.tax,
                })));
            }
        } catch (error) {
            console.error('Error fetching card machine:', error);
            const payload = error.response?.data;
            handleShowErrorSnackbar(
                payload !== undefined ? payload : (error.message || 'Não foi possível carregar a maquininha.')
            );
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => {
        if (id) {
            fetchCardMachine();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // A tabela é regenerada a cada alteração do campo "Máx. parcelas" —
    // aumentar adiciona linhas ao final com taxa sugerida, diminuir remove as
    // últimas, sempre preservando os valores já editados nas linhas que
    // continuam existindo.
    const handleMaxInstallmentChange = (value) => {
        formik.setFieldValue('maxInstallment', value);
        const num = parseInt(value, 10);
        if (!Number.isNaN(num) && num >= 1 && num <= MAX_INSTALLMENT_LIMIT) {
            setInstallmentRates((prev) => regenerateRates(num, prev, formik.values.tax_debit));
        }
    };

    const handleRateInput = (installmentNumber, value) => {
        setInstallmentRates((prev) => prev.map((rate) =>
            rate.installment_number === installmentNumber ? { ...rate, tax: value } : rate
        ));
    };

    const breadcrumbs = [
        {
            title: 'Maquininhas',
            link: '/cardmachines'
        }
    ];

    // ---- Derivados do layout "acordeão + preview ao vivo" (DESIGN-SYSTEM.md
    // §5.5/§6.2), copiado 1:1 de `services/Form.js`, mas com 2 seções: 1)
    // "Dados da maquininha" (obrigatória) e 2) "Taxas por parcelamento" (a
    // matriz dinâmica, sem campos obrigatórios próprios — mesmo racional da
    // seção opcional "Tarefas do serviço").
    const isFieldFilled = (fieldName) => {
        const value = formik.values[fieldName];
        if (value === null || value === undefined) return false;
        return String(value).trim().length > 0;
    };

    const standaloneSections = [
        {
            key: 'mach-1',
            title: 'Dados da maquininha',
            subtitle: 'Nome, parcelamento máximo e taxas base',
            icon: CreditCard,
            requiredFields: ['name', 'maxInstallment', 'tax_debit', 'tax_pixqrcode'],
        },
        {
            key: 'mach-2',
            title: 'Taxas por parcelamento',
            subtitle: 'Uma taxa para cada número de parcelas no crédito',
            icon: Layers,
            requiredFields: [],
        },
    ];
    const requiredStandaloneSections = standaloneSections.filter((sec) => sec.requiredFields.length > 0);
    const completeStandaloneSectionsCount = requiredStandaloneSections.filter(
        (sec) => sec.requiredFields.every(isFieldFilled)
    ).length;
    const progressPct = requiredStandaloneSections.length > 0
        ? Math.round((completeStandaloneSectionsCount / requiredStandaloneSections.length) * 100)
        : 0;
    const RING_R = 23;
    const RING_CIRC = 2 * Math.PI * RING_R;
    const ringOffset = RING_CIRC - (RING_CIRC * progressPct / 100);

    const standaloneFieldSectionMap = {
        name: 'mach-1', maxInstallment: 'mach-1', tax_debit: 'mach-1', tax_pixqrcode: 'mach-1',
    };
    const handleStandaloneSaveClick = async () => {
        const errors = await formik.validateForm();
        const errorFieldKeys = Object.keys(errors);
        if (errorFieldKeys.length > 0) {
            const sectionKeyWithError = errorFieldKeys
                .map((key) => standaloneFieldSectionMap[key])
                .find(Boolean);
            if (sectionKeyWithError) {
                ensureSectionOpen(sectionKeyWithError);
            }
        }
        formik.handleSubmit();
    };

    // Ref: prototipo-telas-insercao.html `.ff-input` — mesma caixa de campo
    // (52px, radius 12px, fundo surface-2, borda 1.5px) e label flutuante
    // (DESIGN-SYSTEM.md §5.5) de `products/Form.js`/`services/Form.js`.
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
        },
        '& .MuiOutlinedInput-input': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
    };

    const standaloneFieldSx = {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };

    // Versão compacta usada nos inputs de taxa da matriz de parcelamento.
    const rateInputSx = {
        '& .MuiInputBase-input': { fontSize: '12.5px', textAlign: 'right', padding: '8px 10px' },
        '& .MuiOutlinedInput-root': {
            borderRadius: '9px',
            backgroundColor: 'background.subtle',
            height: 36,
            '& .MuiOutlinedInput-notchedOutline': { borderWidth: '1px', borderColor: 'divider' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: '1.5px' },
        },
    };

    const requiredFieldLabel = (text) => (
        <>
            {text} <Box component="span" sx={{ color: 'error.main' }}>*</Box>
        </>
    );

    const isEditing = Boolean(id);
    const isLoadingInput = isEditing && loadingData;

    const debitFeeValue = parseFeeInput(formik.values.tax_debit);
    const pixFeeValue = parseFeeInput(formik.values.tax_pixqrcode);
    const sortedRates = installmentRates.slice().sort((a, b) => a.installment_number - b.installment_number);
    let rateRangeLabel = '—';
    if (sortedRates.length === 1) {
        const only = sortedRates[0];
        rateRangeLabel = `${only.installment_number}x: ${pct(parseFeeInput(only.tax) || 0)}`;
    } else if (sortedRates.length > 1) {
        const first = sortedRates[0];
        const last = sortedRates[sortedRates.length - 1];
        rateRangeLabel = `${first.installment_number}x: ${pct(parseFeeInput(first.tax) || 0)} – ${last.installment_number}x: ${pct(parseFeeInput(last.tax) || 0)}`;
    }

    const formContent = (
        <>
            <Box sx={{ px: 0, pb: 0 }}>
                <Box sx={{ mb: 2.5 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '20px',
                            flexWrap: 'wrap',
                            mb: 2.25,
                        }}
                    >
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5 }}>
                                {id ? 'Editar maquininha' : 'Nova maquininha'}
                            </Typography>
                            <Typography sx={{ fontSize: '13.5px', color: 'text.secondary' }}>
                                {id
                                    ? `Atualize os dados de "${formik.values.name || 'maquininha'}".`
                                    : 'Preencha por seções — o cartão ao lado mostra o resultado em tempo real.'}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                            <Box sx={{ position: 'relative', width: 54, height: 54, flexShrink: 0 }}>
                                <svg viewBox="0 0 54 54" style={{ transform: 'rotate(-90deg)', width: '100%', height: '100%' }}>
                                    <circle cx="27" cy="27" r={RING_R} fill="none" strokeWidth="5" stroke={theme.palette.divider} />
                                    <circle
                                        cx="27"
                                        cy="27"
                                        r={RING_R}
                                        fill="none"
                                        strokeWidth="5"
                                        strokeLinecap="round"
                                        stroke={theme.palette.primary.main}
                                        strokeDasharray={RING_CIRC}
                                        strokeDashoffset={ringOffset}
                                        style={{ transition: 'stroke-dashoffset .35s ease' }}
                                    />
                                </svg>
                                <Box
                                    sx={{
                                        position: 'absolute',
                                        inset: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 12,
                                        fontWeight: 800,
                                    }}
                                >
                                    {progressPct}%
                                </Box>
                            </Box>
                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Cadastro</Typography>
                                <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                    completo conforme você preenche
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {standaloneSections.map((sec, idx) => {
                            const isSecOpen = isSectionOpen(sec.key);
                            const isSecComplete = sec.requiredFields.length > 0 && sec.requiredFields.every(isFieldFilled);
                            return (
                                <Box
                                    key={sec.key}
                                    onClick={() => ensureSectionOpen(sec.key)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '7px',
                                        px: '14px',
                                        py: '8px',
                                        borderRadius: '24px',
                                        border: '1.5px solid',
                                        borderColor: isSecOpen ? 'primary.main' : 'divider',
                                        backgroundColor: isSecOpen ? 'info.bg' : 'background.paper',
                                        color: isSecOpen ? 'primary.main' : 'text.secondary',
                                        fontSize: '12px',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 17,
                                            height: 17,
                                            borderRadius: '50%',
                                            border: '2px solid',
                                            borderColor: isSecComplete ? 'success.main' : (isSecOpen ? 'primary.main' : 'text.disabled'),
                                            backgroundColor: isSecComplete ? 'success.main' : 'transparent',
                                            color: isSecComplete ? '#fff' : 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 9,
                                            flexShrink: 0,
                                        }}
                                    >
                                        {isSecComplete ? <Check size={10} strokeWidth={3} /> : idx + 1}
                                    </Box>
                                    {sec.title}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                <form onSubmit={formik.handleSubmit}>
                    {isLoadingInput ? (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px' }}>
                            <Stack spacing={1.75}>
                                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                                <Skeleton variant="rounded" height={220} sx={{ borderRadius: '18px' }} />
                            </Stack>
                            <Skeleton variant="rounded" height={340} sx={{ borderRadius: '20px' }} />
                        </Box>
                    ) : (
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 340px' }, gap: '20px', alignItems: 'start', '& > *': { minWidth: 0 } }}>
                            <Stack spacing={1.75} sx={{ minWidth: 0 }}>
                                <AccordionSection
                                    open={isSectionOpen('mach-1')}
                                    complete={standaloneSections[0].requiredFields.every(isFieldFilled)}
                                    icon={CreditCard}
                                    title="Dados da maquininha"
                                    subtitle="Nome, parcelamento máximo e taxas base"
                                    onToggle={() => toggleSection('mach-1')}
                                >
                                    <Box sx={{ mb: 2 }}>
                                        <TextField
                                            autoFocus={!id}
                                            label={requiredFieldLabel('Nome da maquininha')}
                                            fullWidth
                                            name="name"
                                            value={formik.values.name}
                                            onChange={formik.handleChange}
                                            error={formik.touched.name && Boolean(formik.errors.name)}
                                            helperText={formik.touched.name && formik.errors.name}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2, '& > *': { minWidth: 0 } }}>
                                        <TextField
                                            label={requiredFieldLabel('Máx. parcelas')}
                                            fullWidth
                                            type="number"
                                            name="maxInstallment"
                                            value={formik.values.maxInstallment}
                                            onChange={(e) => handleMaxInstallmentChange(e.target.value)}
                                            error={formik.touched.maxInstallment && Boolean(formik.errors.maxInstallment)}
                                            helperText={formik.touched.maxInstallment && formik.errors.maxInstallment}
                                            inputProps={{ min: 1, max: MAX_INSTALLMENT_LIMIT }}
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <TextField
                                            label={requiredFieldLabel('Taxa débito (%)')}
                                            fullWidth
                                            name="tax_debit"
                                            value={formik.values.tax_debit}
                                            onChange={formik.handleChange}
                                            error={formik.touched.tax_debit && Boolean(formik.errors.tax_debit)}
                                            helperText={formik.touched.tax_debit && formik.errors.tax_debit}
                                            placeholder="0,00"
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                        <TextField
                                            label={requiredFieldLabel('Taxa PIX (%)')}
                                            fullWidth
                                            name="tax_pixqrcode"
                                            value={formik.values.tax_pixqrcode}
                                            onChange={formik.handleChange}
                                            error={formik.touched.tax_pixqrcode && Boolean(formik.errors.tax_pixqrcode)}
                                            helperText={formik.touched.tax_pixqrcode && formik.errors.tax_pixqrcode}
                                            placeholder="0,00"
                                            variant="outlined"
                                            sx={standaloneFieldSx}
                                        />
                                    </Box>
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1.25 }}>
                                        Defina quantas parcelas de crédito essa maquininha aceita — a tabela de taxas por parcela na próxima seção se ajusta automaticamente ao número informado aqui.
                                    </Typography>
                                </AccordionSection>

                                <AccordionSection
                                    open={isSectionOpen('mach-2')}
                                    complete={false}
                                    icon={Layers}
                                    title="Taxas por parcelamento"
                                    subtitle="Uma taxa para cada número de parcelas no crédito"
                                    onToggle={() => toggleSection('mach-2')}
                                >
                                    {installmentRates.length === 0 ? (
                                        <Typography sx={{ fontSize: '13px', color: 'text.disabled' }}>
                                            Informe o "Máx. parcelas" na seção anterior para gerar a tabela de taxas.
                                        </Typography>
                                    ) : (
                                        <Box sx={{ maxHeight: 340, overflowY: 'auto', pr: '4px' }}>
                                            <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '12.5px' }}>
                                                <Box component="thead">
                                                    <Box component="tr">
                                                        <Box component="th" sx={{ textAlign: 'left', color: 'text.disabled', fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.05em', pb: 1, fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider', width: '40%' }}>
                                                            Parcelas
                                                        </Box>
                                                        <Box component="th" sx={{ textAlign: 'left', color: 'text.disabled', fontSize: '10.5px', textTransform: 'uppercase', letterSpacing: '.05em', pb: 1, fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
                                                            Taxa de crédito (%)
                                                        </Box>
                                                    </Box>
                                                </Box>
                                                <Box component="tbody">
                                                    {sortedRates.map((rate) => (
                                                        <Box component="tr" key={rate.installment_number}>
                                                            <Box component="td" sx={{ py: '6px', borderBottom: '1px solid', borderColor: 'divider', fontWeight: 700, color: 'text.primary', fontSize: '13px', whiteSpace: 'nowrap' }}>
                                                                {rate.installment_number}x
                                                            </Box>
                                                            <Box component="td" sx={{ py: '6px', borderBottom: '1px solid', borderColor: 'divider' }}>
                                                                <TextField
                                                                    value={rate.tax}
                                                                    onChange={(e) => handleRateInput(rate.installment_number, e.target.value)}
                                                                    size="small"
                                                                    fullWidth
                                                                    variant="outlined"
                                                                    sx={{ maxWidth: 160, ...rateInputSx }}
                                                                    InputProps={{
                                                                        endAdornment: <Typography sx={{ fontSize: 12, color: 'text.disabled', ml: 0.5 }}>%</Typography>
                                                                    }}
                                                                />
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                    <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1.25 }}>
                                        A quantidade de linhas é definida pelo campo "Máx. parcelas" da seção anterior. Ao aumentar, novas linhas entram com uma taxa sugerida (editável); ao diminuir, as últimas linhas são removidas e os valores já preenchidos das demais são preservados.
                                    </Typography>
                                </AccordionSection>
                            </Stack>

                            <Box sx={{ position: { md: 'sticky' }, top: { md: 20 } }}>
                                <Typography
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        fontSize: '10.5px',
                                        fontWeight: 800,
                                        textTransform: 'uppercase',
                                        letterSpacing: '.06em',
                                        color: 'text.disabled',
                                        mb: 1.25,
                                    }}
                                >
                                    Pré-visualização ao vivo
                                </Typography>

                                <Box
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: `${theme.customRadius.hero - 2}px`,
                                        backgroundColor: 'background.paper',
                                        boxShadow: theme.customShadows.md,
                                        overflow: 'hidden',
                                        mb: 1.75,
                                    }}
                                >
                                    <Box sx={{ height: 78, backgroundImage: theme.palette.gradient.brand, position: 'relative' }}>
                                        <Box sx={{
                                            width: 72,
                                            height: 72,
                                            borderRadius: '22px',
                                            backgroundColor: 'background.paper',
                                            border: '4px solid',
                                            borderColor: 'background.paper',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: 'primary.main',
                                            position: 'absolute',
                                            left: 20,
                                            bottom: -32,
                                            boxShadow: theme.customShadows.md,
                                        }}>
                                            <CreditCard size={26} strokeWidth={2} />
                                        </Box>
                                    </Box>
                                    <Box sx={{ p: '42px 20px 20px' }}>
                                        <Typography sx={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>
                                            {formik.values.name || 'Nome da maquininha'}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'text.disabled', mt: '2px' }}>
                                            {formik.values.name ? 'Maquininha de cartão' : 'Preencha os dados ao lado'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: 1.5 }}>
                                            <Box component="span" sx={{ fontSize: 11, fontWeight: 700, px: '10px', py: '5px', borderRadius: '20px', bgcolor: 'info.bg', color: 'primary.main' }}>
                                                Máx. {formik.values.maxInstallment || '—'}x
                                            </Box>
                                        </Box>
                                        <Box sx={{ height: '1px', bgcolor: 'divider', my: 2 }} />
                                        <PreviewRow label="Taxa débito" value={Number.isNaN(debitFeeValue) ? '—' : pct(debitFeeValue)} />
                                        <PreviewRow label="Taxa PIX" value={Number.isNaN(pixFeeValue) ? '—' : pct(pixFeeValue)} />
                                        <PreviewRow label="Faixa de taxas (crédito)" value={rateRangeLabel} />
                                    </Box>
                                </Box>

                                <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>
                                    Campos com <Box component="span" sx={{ color: 'error.main' }}>*</Box> são obrigatórios. Você pode editar tudo depois de salvar.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* Espaçador para compensar o rodapé fixo abaixo. */}
                    <Box sx={{ height: 'calc(84px + env(safe-area-inset-bottom, 0px))' }} aria-hidden="true" />

                    <Box
                        sx={{
                            position: 'fixed',
                            left: actionFooterLeft,
                            right: 0,
                            bottom: 0,
                            zIndex: theme.zIndex.appBar,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 2,
                            flexWrap: 'wrap',
                            backgroundColor: 'background.paper',
                            borderTop: '1px solid',
                            borderColor: 'divider',
                            boxShadow: '0 -6px 20px rgba(15,23,42,.06)',
                            px: 3,
                            py: 1.75,
                            pl: 'calc(24px + env(safe-area-inset-left, 0px))',
                            pr: 'calc(24px + env(safe-area-inset-right, 0px))',
                            pb: 'calc(14px + env(safe-area-inset-bottom, 0px))',
                            transition: 'left .2s ease',
                        }}
                    >
                        <Typography sx={{ fontSize: '12.5px', color: 'text.disabled' }}>
                            Campos com * são obrigatórios
                        </Typography>
                        <Stack direction="row" spacing={1.5}>
                            <Button variant="outlined" onClick={() => navigate('/cardmachines')}>
                                Cancelar
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleStandaloneSaveClick}
                                disabled={loading}
                            >
                                {loading ? 'Salvando...' : (id ? 'Salvar alterações' : 'Salvar maquininha')}
                            </Button>
                        </Stack>
                    </Box>
                </form>
            </Box>
        </>
    );

    return (
        <PageScaffold
            loading={loading}
            title={id ? 'Editar Maquininha' : 'Nova Maquininha'}
            titlePageTarget={id ? 'Editar' : 'Nova'}
            links={breadcrumbs}
            openSnackbar={openSnackbar}
            setOpenSnackbar={setOpenSnackbar}
            message={message}
            severity={severity}
        >
            {isMobileShell ? (
                isLoadingInput ? (
                    <Stack spacing={1.25}>
                        {[1, 2, 3].map((n) => <Skeleton key={n} variant="rounded" height={64} sx={{ borderRadius: '16px' }} />)}
                    </Stack>
                ) : (
                    <MobileCardMachineForm
                        formik={formik}
                        maxInstallmentLimit={MAX_INSTALLMENT_LIMIT}
                        onMaxInstallmentChange={handleMaxInstallmentChange}
                        installmentRates={installmentRates}
                        onRateChange={handleRateInput}
                        onSave={handleStandaloneSaveClick}
                        onCancel={() => navigate('/cardmachines')}
                        loading={loading}
                    />
                )
            ) : formContent}
        </PageScaffold>
    );
}
